class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name^="updates["]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    // events
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // dev events
    document.addEventListener('ajaxProduct:added', () => {
      this.renderAndShowItems()
    })

    // Free shipping check onload
    this.updateShippingNotification('cart-shipping-notification');
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event
   */
  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionItemsToRender() {
    return ['cart-items','cart-shipping-notification', 'cart-priority-shipping-banner', 'cart-recommendations', 'cart-note', 'cart-footer', 'cart-upsell-mobile', 'cart-upsell-mobile'];
  }

  /**
   * This function is always called when the cart is updated.
   * @param {string} json
   */
  updateCartSections (newHTML) {
    this.getSectionItemsToRender().forEach((item) => {
      const elementToReplace = document.getElementById(item);

      if (!elementToReplace) return;

      // the free-shipping bar animates from where it was, so remember that before swapping
      const previousShippingProgress = item === 'cart-shipping-notification'
        ? this.readShippingProgress(elementToReplace)
        : null;

      // replace old with new
      elementToReplace.innerHTML = newHTML.getElementById(item).innerHTML;

      if (item === 'cart-shipping-notification') {
        this.updateShippingNotification('cart-shipping-notification', previousShippingProgress);
      }
    });

    // --- Update <cart-frame-upsell> elements ---
    const currentUpsells = document.querySelectorAll('cart-frame-upsell');
    const newUpsells = newHTML.querySelectorAll('cart-frame-upsell');

    if (currentUpsells.length && newUpsells.length) {
      currentUpsells.forEach((el, i) => {
        if (newUpsells[i]) {
          el.outerHTML = newUpsells[i].outerHTML;
        }
      });
    }
    // --- End update <cart-frame-upsell> ---

    // Trigger calculator updates after cart sections are updated
    const event = new CustomEvent('cart:calculators:update');
    document.dispatchEvent(event);
  }

  /**
   * Free-shipping progress (snippets/cart-shipping-notification.liquid).
   * Handles multi-currency: the threshold is in the shop currency, the cart total in the customer's,
   * so the total is converted back to the shop currency before comparing.
   * Called on load and every time the cart sections re-render.
   * @param {string} selector - id of the section-render wrapper
   * @param {number|null} fromProgress - progress (0..1) shown before the re-render, to animate from
   */
  updateShippingNotification(selector, fromProgress = null) {
    const shippingNotificationWrapper = document.getElementById(selector);

    if (!shippingNotificationWrapper) {
      return;
    }

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');
    if (!shippingNotification) return;

    let cartTotal = parseInt(shippingNotification.dataset.cartTotal, 10),
        freeShippingTreshold = parseInt(shippingNotification.dataset.threshold,10),
        shopCurrency = shippingNotification.dataset.shopCurrency,
        targetCurrency = shippingNotification.dataset.targetCurrency;

    if (shopCurrency !== targetCurrency) {
      cartTotal = Math.round(cartTotal / (parseFloat(Shopify.currency.rate) || 1 ));
    }

    const reached = cartTotal >= freeShippingTreshold;
    const progress = reached || freeShippingTreshold <= 0 ? 1 : Math.max(0, Math.min(1, cartTotal / freeShippingTreshold));
    const textElement = shippingNotification.querySelector('[data-free-shipping-text]');
    const road = shippingNotification.querySelector('[role="progressbar"]');

    if (textElement && !reached) {
      const template = shippingNotification.dataset.msgProgress || window.cartStrings.shipping_not_free;
      const amount = Woolman.Utils.formatMoney(freeShippingTreshold - cartTotal);
      textElement.innerHTML = template.replace('[amount]', `<strong>${amount}</strong>`);
    }
    shippingNotification.classList.toggle('threshold-reached', reached);
    if (road) road.setAttribute('aria-valuenow', Math.round(progress * 100));

    const from = typeof fromProgress === 'number' ? fromProgress : progress;
    shippingNotification.style.setProperty('--free-ship-progress', from);
    if (from === progress) return;

    // paint the old position first, then let the bar grow/shrink to the new one
    void shippingNotification.offsetWidth;
    requestAnimationFrame(() => {
      shippingNotification.style.setProperty('--free-ship-progress', progress);
    });
  }

  /**
   * Progress (0..1) the free-shipping bar currently shows, or null when there is none.
   * @param {HTMLElement} wrapper
   */
  readShippingProgress(wrapper) {
    const bar = wrapper && wrapper.querySelector('[data-free-shipping-notification]');
    if (!bar) return null;
    if (bar.hidden) return 0; // empty cart: the first item drives in from the start
    const value = parseFloat(bar.style.getPropertyValue('--free-ship-progress'));
    return Number.isNaN(value) ? null : value;
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderAndShowItems() {
    const parentDrawer = this.closest('[data-drawer]');

    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);

        if (!parentDrawer) return;
        Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line
   * @param {*} quantity
   * @param {*} name
   */
  /**
   * A poster line with linked accessories (frame, passe-partout, hanger rendered as
   * .cart-addon lines inside it) moves them along: every accessory whose quantity matched
   * the poster's gets the poster's new quantity in the same request, so removing the poster
   * removes its frame too. Returns null when the line has no linked accessories.
   */
  linkedUpdates(line, quantity) {
    const lineItem = document.getElementById(`CartItem-${line}`);
    if (!lineItem || !lineItem.dataset.lineKey) return null;
    const addons = lineItem.querySelectorAll('.cart-addon[data-addon-key]');
    if (!addons.length) return null;
    const previous = parseInt(lineItem.dataset.quantity, 10);
    const next = Math.max(0, parseInt(quantity, 10) || 0);
    const updates = { [lineItem.dataset.lineKey]: next };
    let linked = 0;
    addons.forEach((addon) => {
      if (parseInt(addon.dataset.quantity, 10) === previous) {
        updates[addon.dataset.addonKey] = next;
        linked += 1;
      }
    });
    return linked ? updates : null;
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const linkedUpdates = this.linkedUpdates(line, quantity);
    const request = linkedUpdates
      ? { url: `${routes.cart_update_url}.js`, body: { updates: linkedUpdates } }
      : { url: `${routes.cart_change_url}`, body: { line: line, quantity: quantity } };
    fetch(request.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({
        ...request.body,
        sections: 'main-cart',
        sections_url: window.location.pathname,
      }),
    })
    .then((response) => response.json())
    .then((responseJson) => {

      const quantityElement = document.getElementById(`Quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      if (responseJson.errors || (linkedUpdates && responseJson.status && !responseJson.sections)) {
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, responseJson.errors || responseJson.description || responseJson.message);
        return;
      }

      const event = new CustomEvent('cart:update', { detail: responseJson,});
      document.dispatchEvent(event);

      // Render new items according to response
      const html = new DOMParser().parseFromString(responseJson.sections['main-cart'], 'text/html');
      this.updateCartSections(html);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.disableLoading();
    });
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line
   * @param {*} message
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      lineItemError.classList.remove('hide');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)

        const res = await fetch(url)
        const { products } = await res.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        products.forEach((product) => {

          const template = this.querySelector('#template-recommendation-item').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const lastDotIndex = filename.lastIndexOf('.');
          const newFilename = `${filename.substring(0, lastDotIndex)}_300x${filename.substring(lastDotIndex)}`;
          const url = product.featured_image.replace(filename, newFilename);

          template.querySelector('img').setAttribute('src', url);
          template.querySelector('.card-media').setAttribute('href', location.origin + product.url);
          template.setAttribute('title', product.title);
          template.querySelector('.card-title a').textContent = product.title;
          template.querySelector('.card-title a').setAttribute('href', location.origin + product.url);

          this.productsContainer.appendChild(template);
        })
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}


//cart banner add to cart function
document.getElementById('addToCartBtn')?.addEventListener('click', function(e) {
  const variantId = this.dataset.variantId;
  e.preventDefault();
  e.stopPropagation();
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  })
  .then(res => res.json())
  .then(data => {
    console.log('Added to cart', data);
    // yaha cart drawer open kar sakte ho

    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
  });
});
/**
 * "Tilføj ramme" on a poster line without a frame (snippets/cart-items.liquid): toggles an
 * inline picker of the frames that exist in the poster's size; picking one adds that frame
 * variant at the poster's quantity, linked via `_Poster to frame` (the same property the
 * product-page frame selector writes), then lets the theme re-render the cart so the frame
 * shows up grouped under its poster.
 */
if (!customElements.get('cart-frame-add')) {
  customElements.define('cart-frame-add', class CartFrameAdd extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.toggle = this.querySelector('[data-toggle]');
      this.panel = this.querySelector('[data-panel]');
      this.error = this.querySelector('[data-error]');
      this.toggle?.addEventListener('click', () => this.setOpen(this.panel.hidden));
      this.querySelector('[data-cancel]')?.addEventListener('click', () => this.setOpen(false));
      // NB: the swatch buttons also carry data-label — target the label element itself.
      this.label = this.querySelector('.cart-frame-add__label');
      this.querySelectorAll('[data-variant-id]').forEach((btn) => {
        btn.addEventListener('click', () => this.add(btn));
        // The small label next to the swatches names the hovered / focused frame and its price.
        btn.addEventListener('mouseenter', () => this.showLabel(btn.dataset.label));
        btn.addEventListener('focus', () => this.showLabel(btn.dataset.label));
        btn.addEventListener('mouseleave', () => this.showLabel());
        btn.addEventListener('blur', () => this.showLabel());
      });
    }

    showLabel(html) {
      if (this.label) this.label.innerHTML = html || this.label.dataset.default || '';
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      this.panel.hidden = !open;
      this.toggle.hidden = open;
      this.toggle.setAttribute('aria-expanded', String(open));
      if (this.error) this.error.hidden = true;
      if (!open) this.toggle.focus();
    }

    async add(btn) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-busy');
      btn.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      const quantity = Math.max(1, parseInt(this.dataset.quantity, 10) || 1);
      try {
        const res = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(btn.dataset.variantId),
                quantity,
                // Personal posters also carry their line id, so a frame lands on the right one.
                properties: Object.assign({ '_Poster to frame': this.dataset.posterSku }, this.dataset.posterId ? { '_Poster ID': this.dataset.posterId } : {}),
              },
            ],
          }),
        });
        if (!res.ok) throw new Error(`cart/add ${res.status}`);
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', { detail: { variantId: Number(btn.dataset.variantId), quantity } })
        );
      } catch (e) {
        console.error(e);
        if (this.error) this.error.hidden = false;
        this.busy = false;
        this.classList.remove('is-busy');
        btn.removeAttribute('aria-busy');
      }
    }
  });
}
